import { createAsyncThunk } from "@reduxjs/toolkit";
import { loadingActions } from "../LoaderSlice";
import { modalActions } from "../ModalSlice";
import { ONBOARDING_API_URL } from "../../constants";
import axiosClient from "../../utils/axiosClient";
import { normalizeOnboardingId } from "../../utils/onboardingHelpers";

const getOnboardingBaseUrl = () => "https://" + ONBOARDING_API_URL;

const handleApiError = (dispatch, error, label) => {
  console.log(`Error ${label} : `, error?.message || error);
  dispatch(loadingActions.setLoading(false));
  dispatch(
    modalActions.setErrorModal({
      message: error?.message || "Something went wrong",
      status: error?.status || "error",
      state: true,
    }),
  );
};

const handleBusinessError = (dispatch, responseData) => {
  dispatch(
    modalActions.setErrorModal({
      message:
        "Error : " +
        (responseData?.message ||
          responseData?.error_message ||
          "Request failed"),
      status: "error",
      state: true,
    }),
  );
};

const postOnboardingAction = async ({
  action,
  payload = {},
  dispatch,
  rejectWithValue,
  showLoader = true,
  showErrorModal = true,
  label,
}) => {
  if (showLoader) dispatch(loadingActions.setLoading(true));

  const url = getOnboardingBaseUrl();
  const requestPayload = { action, ...payload };

  console.log(`\n\nInside ${label} : `, requestPayload);

  try {
    const response = await axiosClient.post(url, requestPayload);
    if (showLoader) dispatch(loadingActions.setLoading(false));
    console.log(`${label} response : `, response.data);

    const responseData = response.data;
    const nestedBody =
      typeof responseData?.body === "string"
        ? (() => {
            try {
              return JSON.parse(responseData.body);
            } catch (error) {
              return null;
            }
          })()
        : responseData?.body;
    const businessStatus = String(
      nestedBody?.status || responseData?.status || "",
    ).toLowerCase();

    if (businessStatus === "error" || businessStatus === "failed") {
      if (showLoader) dispatch(loadingActions.setLoading(false));
      if (showErrorModal) {
        handleBusinessError(dispatch, nestedBody || responseData);
      }
      return rejectWithValue(
        nestedBody?.error_message ||
          nestedBody?.message ||
          responseData?.error_message ||
          responseData?.message,
      );
    }

    return responseData;
  } catch (error) {
    if (showErrorModal) {
      handleApiError(dispatch, error, label);
    } else if (showLoader) {
      dispatch(loadingActions.setLoading(false));
    }
    return rejectWithValue(error);
  }
};

export const getCandidateApprovalSummary = createAsyncThunk(
  "onboarding/get_candidate_approval_summary",
  async (payload = {}, { dispatch, rejectWithValue }) => {
    const pageNumber = Number(payload.page);
    return postOnboardingAction({
      action: "get_candidate_approval_summary",
      payload: {
        page: Number.isFinite(pageNumber) && pageNumber > 0 ? pageNumber : 1,
      },
      dispatch,
      rejectWithValue,
      label: "getCandidateApprovalSummary",
    });
  },
);

export const getCandidateApprovalDetails = createAsyncThunk(
  "onboarding/get_candidate_approval_details",
  async (payload = {}, { dispatch, rejectWithValue }) => {
    return postOnboardingAction({
      action: "get_candidate_approval_details",
      payload: {
        onboarding_id: normalizeOnboardingId(payload.onboarding_id),
      },
      dispatch,
      rejectWithValue,
      label: "getCandidateApprovalDetails",
    });
  },
);

export const copyEmployeeDetails = createAsyncThunk(
  "onboarding/copy_employee_details",
  async (payload = {}, { dispatch, rejectWithValue }) => {
    return postOnboardingAction({
      action: "copy_employee_details",
      payload: {
        employee_id: String(payload.employee_id || "").trim(),
      },
      dispatch,
      rejectWithValue,
      label: "copyEmployeeDetails",
    });
  },
);

export const getJobDetailsDropdown = createAsyncThunk(
  "onboarding/get_job_details_dropdown",
  async (payload = {}, { dispatch, rejectWithValue }) => {
    return postOnboardingAction({
      action: "get_job_details_dropdown",
      payload: {
        onboarding_id: normalizeOnboardingId(payload.onboarding_id),
      },
      dispatch,
      rejectWithValue,
      showLoader: false,
      label: "getJobDetailsDropdown",
    });
  },
);

export const saveOnboardingProgress = createAsyncThunk(
  "onboarding/save_progress",
  async (payload = {}, { dispatch, rejectWithValue }) => {
    const {
      action: _ignoredAction,
      onboarding_id,
      ...rest
    } = payload || {};

    return postOnboardingAction({
      action: "save_progress",
      payload: {
        onboarding_id: normalizeOnboardingId(onboarding_id),
        ...rest,
      },
      dispatch,
      rejectWithValue,
      label: "saveOnboardingProgress",
    });
  },
);

export const sendBackForCorrection = createAsyncThunk(
  "onboarding/send_back_for_correction",
  async (payload = {}, { dispatch, rejectWithValue }) => {
    const {
      action: _ignoredAction,
      onboarding_id,
      ...rest
    } = payload || {};

    return postOnboardingAction({
      action: "send_back_for_correction",
      payload: {
        onboarding_id: normalizeOnboardingId(onboarding_id),
        ...rest,
      },
      dispatch,
      rejectWithValue,
      label: "sendBackForCorrection",
    });
  },
);

export const markAsReviewedAndCreateId = createAsyncThunk(
  "onboarding/mark_as_reviewed_and_create_id",
  async (payload = {}, { dispatch, rejectWithValue }) => {
    return postOnboardingAction({
      action: "mark_as_reviewed_and_create_id",
      payload: {
        onboarding_id: normalizeOnboardingId(payload.onboarding_id),
        employee_id: String(payload.employee_id || "").trim(),
      },
      dispatch,
      rejectWithValue,
      label: "markAsReviewedAndCreateId",
    });
  },
);

export const generatePresignUrl = createAsyncThunk(
  "onboarding/generate_presign_url",
  async (payload = {}, { dispatch, rejectWithValue }) => {
    return postOnboardingAction({
      action: "generate_presign_url",
      payload: {
        file_key: String(payload.file_key || "").trim(),
      },
      dispatch,
      rejectWithValue,
      label: "generatePresignUrl",
    });
  },
);

/** HR Direct Edit — field/document history (local UI errors, no global modal). */
export const getHrEditHistory = createAsyncThunk(
  "onboarding/get_hr_edit_history",
  async (payload = {}, { dispatch, rejectWithValue }) => {
    const requestPayload = {
      onboarding_id: normalizeOnboardingId(payload.onboarding_id),
      field_key: String(payload.field_key || "").trim(),
      section_key: String(payload.section_key || "").trim().toLowerCase(),
    };
    if (
      requestPayload.section_key === "education" ||
      requestPayload.section_key === "employment"
    ) {
      const rawIndex =
        payload.record_index != null ? payload.record_index : payload.recordIndex;
      if (rawIndex != null && rawIndex !== "") {
        const recordIndex = Number(rawIndex);
        if (Number.isFinite(recordIndex)) {
          requestPayload.record_index = recordIndex;
        }
      }
    }
    return postOnboardingAction({
      action: "get_hr_edit_history",
      payload: requestPayload,
      dispatch,
      rejectWithValue,
      showLoader: false,
      showErrorModal: false,
      label: "getHrEditHistory",
    });
  },
);

/** HR document replace — create_presigned_post (loader owned by caller). */
export const createPresignedPost = createAsyncThunk(
  "onboarding/create_presigned_post",
  async (payload = {}, { dispatch, rejectWithValue }) => {
    return postOnboardingAction({
      action: "create_presigned_post",
      payload: {
        onboarding_id: normalizeOnboardingId(payload.onboarding_id),
        doc_ref_id:
          payload.doc_ref_id == null ? "" : String(payload.doc_ref_id),
        firstname: String(payload.firstname || "").trim(),
        lastname: String(payload.lastname || "").trim(),
        document_type: payload.document_type,
        filename: String(payload.filename || "").trim(),
        upload_for: payload.upload_for || "HR",
      },
      dispatch,
      rejectWithValue,
      showLoader: false,
      showErrorModal: false,
      label: "createPresignedPost",
    });
  },
);

/** HR document replace — finalize after S3 upload (loader owned by caller). */
export const hrReplaceDocument = createAsyncThunk(
  "onboarding/hr_replace_document",
  async (payload = {}, { dispatch, rejectWithValue }) => {
    const requestPayload = {
      onboarding_id: normalizeOnboardingId(payload.onboarding_id),
      document_type: payload.document_type,
      filename: String(payload.filename || "").trim(),
    };
    const sectionKey = String(payload.section_key ?? payload.sectionKey ?? "")
      .trim()
      .toLowerCase();
    if (sectionKey) {
      requestPayload.section_key = sectionKey;
    }
    const rawRecordIndex =
      payload.record_index != null ? payload.record_index : payload.recordIndex;
    if (rawRecordIndex != null && rawRecordIndex !== "") {
      const recordIndex = Number(rawRecordIndex);
      if (Number.isFinite(recordIndex)) {
        requestPayload.record_index = recordIndex;
      }
    }
    if (
      payload.version != null &&
      payload.version !== "" &&
      Number.isFinite(Number(payload.version))
    ) {
      requestPayload.version = Number(payload.version);
    }

    return postOnboardingAction({
      action: "hr_replace_document",
      payload: requestPayload,
      dispatch,
      rejectWithValue,
      showLoader: false,
      showErrorModal: false,
      label: "hrReplaceDocument",
    });
  },
);

/** HR Direct Edit — pincode city/state lookup (field-local errors). */
export const getPincodeDetails = createAsyncThunk(
  "onboarding/get_pincode_details",
  async (payload = {}, { dispatch, rejectWithValue }) => {
    return postOnboardingAction({
      action: "get_pincode_details",
      payload: {
        pincode: payload.pincode,
      },
      dispatch,
      rejectWithValue,
      showLoader: false,
      showErrorModal: false,
      label: "getPincodeDetails",
    });
  },
);

/** HR Direct Edit — education master search (field-local errors). */
export const fetchEducationMaster = createAsyncThunk(
  "onboarding/fetch_education_master",
  async (payload = {}, { dispatch, rejectWithValue }) => {
    const requestPayload = {
      category: payload.category,
    };
    const trimmedSearch = String(payload.search || "").trim();
    if (trimmedSearch) requestPayload.search = trimmedSearch;

    return postOnboardingAction({
      action: "fetch_education_master",
      payload: requestPayload,
      dispatch,
      rejectWithValue,
      showLoader: false,
      showErrorModal: false,
      label: "fetchEducationMaster",
    });
  },
);
